import axios from "axios";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8001/api",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

/**
 * Get the current OIDC access token from storage.
 * Checks both the standard and v7 storage formats.
 */
function getOidcAccessToken() {
  // Try the standard oidc.user storage key first
  const oidcStorage =
    sessionStorage.getItem(`oidc.user:${window.location.origin}`) ||
    localStorage.getItem(`oidc.user:${window.location.origin}`);

  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      if (oidcData.access_token) {
        return { token: oidcData.access_token, expiresAt: oidcData.expires_at };
      }
    } catch (error) {
      console.error("Error parsing OIDC storage:", error);
    }
  }

  // Fallback: try the v7 storage format (oidc.default key)
  try {
    const configurationName = "default";
    const oidcTokensKey = `oidc.${configurationName}`;
    const tokensString =
      sessionStorage.getItem(oidcTokensKey) ||
      localStorage.getItem(oidcTokensKey);

    if (tokensString) {
      const tokens = JSON.parse(tokensString);
      const token = tokens.tokens?.accessToken || tokens.accessToken;
      if (token) {
        return {
          token,
          expiresAt: tokens.tokens?.expiresAt || tokens.expiresAt,
        };
      }
    }
  } catch (e) {
    // No token available from fallback
  }

  return null;
}

// Request interceptor to add Bearer token from OIDC storage
axiosInstance.interceptors.request.use(
  (config) => {
    const oidcToken = getOidcAccessToken();
    if (oidcToken?.token) {
      config.headers.Authorization = `Bearer ${oidcToken.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401/403 with token refresh retry
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const currentToken = getOidcAccessToken();

      if (currentToken) {
        const now = Math.floor(Date.now() / 1000);

        // If token is expired or expiring soon, wait for OIDC silent refresh
        if (currentToken.expiresAt && currentToken.expiresAt - now < 60) {
          // Wait a bit for the OIDC library's automatic silent renewal
          await new Promise((resolve) => setTimeout(resolve, 2000));

          const refreshedToken = getOidcAccessToken();

          // Check if we got a new, valid token
          if (
            refreshedToken?.token &&
            refreshedToken.token !== currentToken.token &&
            refreshedToken.expiresAt > now
          ) {
            originalRequest.headers.Authorization = `Bearer ${refreshedToken.token}`;
            return axiosInstance(originalRequest);
          }
        } else {
          // Token isn't expired but we got 401/403 — could be a server-side
          // issue (revoked token, audience mismatch, etc.)
          // Wait briefly and retry once with the current token
          await new Promise((resolve) => setTimeout(resolve, 500));

          const retryToken = getOidcAccessToken();
          if (retryToken?.token && retryToken.token !== currentToken.token) {
            // Token was refreshed in the meantime
            originalRequest.headers.Authorization = `Bearer ${retryToken.token}`;
            return axiosInstance(originalRequest);
          }
        }
      }

      // If we get here, we couldn't recover — let the error propagate
      // AuthGuard or the calling component will handle the UI
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
